import useResizeObserver from "@react-hook/resize-observer";
import { produce } from "immer";
import React, { useCallback, useEffect, useState } from "react";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import styled from "styled-components";
import { useImmerReducer } from "use-immer";

import { getExamItemIdAtPosition, shuffleArray } from "@examspark/common";
import { Callout, Toolbar } from "@examspark/common-ui";
import { RichContentOptionsProvider } from "@examspark/common-ui-rce5";
import {
  ExamConfigContext,
  ExamFlagsContext,
  ExamToolsContext,
} from "@examspark/exam-ui-context";
import {
  ExamBlobs,
  ExamConfigData,
  ExamHighlights,
  ExamScore,
  ExamSessionMetadata,
  ParsedExam,
  Resource,
} from "@examspark/types";

import Button from "./components/common/Button";
import Calculator from "./components/common/tools/Calculator";
import { validateSupportedCalculatorType } from "./components/common/utils/calculator";
import {
  defaultState,
  ExamContext,
  ExamState,
  FontSize,
} from "./components/exam/context/examContext";
import { ExamReducerActions, reducer } from "./components/exam/context/reducer";
import Header from "./components/exam/Header";
import Navigation from "./components/exam/Navigation";
import NoResults from "./components/exam/results/NoResults";
import Results from "./components/exam/results/Results";
import Tools from "./components/exam/Tools";
import Item from "./components/item/Item";
import Modal from "./components/modal/Modal";
import { useAutoSave } from "./hooks/useAutoSave";
import { LineReader } from "./utils/lineReader";
import { itemSupportsResponseMasking } from "./utils/responseMasking";
import { ItemPosition } from "./utils/sections";
import { HighlighterContext } from "./utils/useHighlighter";
import { LineReaderContext } from "./utils/useLineReader";

const StyledFullScreen = styled(FullScreen)`
  height: 100%;

  &.fullscreen-enabled > div > div {
    padding: 1rem;
  }
`;

const StyledExam = styled.div<{ padding?: boolean }>`
  box-sizing: border-box;
  display: flex;
  min-height: 0;
  min-width: 0;
  background-color: #fff;
  max-width: 230rem;
  flex: 1;

  ${(props) => {
    if (props.padding) {
      return `
      padding: 1rem;
      `;
    }
  }}
  flex-direction: column;
  gap: 1rem;
  height: 100%;
  /* flex: 1; */
  /* box-shadow: 0rem 0rem 0.3rem rgb(0 0 0 / 30%); */
`;

const StyledItemNameCallout = styled(Callout)`
  margin-bottom: 1rem;
`;

const Container = styled.div`
  justify-content: center;
  align-items: center;
  background-color: #fff;
  height: 100%;
  min-height: 0;
  display: flex;
`;

const ExamMain = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  flex: 1;
  min-height: 0;
  height: 100%;
`;
const ExamMainColumn = styled.div`
  flex: 1;
  gap: 1rem;
  display: flex;
  min-height: 0;
  min-width: 200px;
  height: 100%;
  flex-direction: column;
`;

const ExamSideColumn = styled.div`
  width: 200px;
  overflow: auto;
`;
const ExamTools = styled.div`
  width: 40px;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const PrimaryButton = styled(Button)`
  background-color: #006699;
  color: white;
  border: none;
  cursor: pointer;
  text-transform: uppercase;
  font-weight: bold;

  &:hover {
    background-color: #0170a7;
  }
`;

const StyledMessage = styled.div`
  padding: 2rem;
`;

const SecondaryButton = styled(Button)`
  background-color: #e7e7e7;
  color: #333;
  display: flex;
  align-items: center;
  font-weight: bold;
  text-transform: uppercase;
  height: 4rem;
  transform: background-color 0.2s;

  &:hover {
    background-color: #e0e0e0;
  }
`;

interface ItemContainerProps {
  fontSize: FontSize;
}

const ItemContainer = styled.div<ItemContainerProps>`
  font-size: ${(props) => {
    switch (props.fontSize) {
      case "normal":
        return `1.4rem`;
      case "large":
        return `1.8rem`;
      case "xlarge":
        return `2.2rem`;
      case "huge":
        return `2.6rem`;
    }
  }};
  height: 100%;
  transition: font-size 0.5s ease;
`;

interface ExamPlayerProps {
  allowFeedback?: boolean;
  autoStart?: boolean;
  config?: ExamConfigData;
  /** Contains exam and question data used to effectively play the exam */
  examData?: any;
  /* Contains (optional) Exam Session data for cases where that may affect item
   * rendering (currently just shuffling but could one day include things like
   * IEP and other accommodations)
   */
  sesssionData?: ExamSessionMetadata;
  flags: Partial<{
    textToSpeech: boolean;
    lineReader: boolean;
    highlighter: boolean;
    responseMasking: boolean;
    tiCalculator: boolean;
  }>;
  onRequestEditItem?: (itemId: string) => void;
  onSave?: (state: ExamState) => void;
  onExit?: () => void;
  onSubmit?: (state: ExamState) => Promise<void>;
  onRequestRetake?: () => void;
  onNavigation?: (position: ItemPosition) => void;
  disableSubmit?: boolean;
  disablePause?: boolean;
  padding?: boolean;
  previewUserId?: string;

  // These props only affect the INITIAL state of these values. Use
  // the reducer to change these values after the exam has been initialized.
  responses?: { [key: string]: any };
  position?: ItemPosition;
  blobs?: ExamBlobs;
  highlights?: ExamHighlights;

  isPaused?: boolean;

  results?: ExamScore;
  itemPreviewer?: any;
  view?: "instructor" | "student";

  resultsToolbar?: React.ReactNode;
}

export const getCurrentItem = (state: ExamState) => {
  if (!state?.exam?.content?.forms?.[state.position.form]) {
    return;
  }

  const form = state.exam.content.forms[state.position.form];
  const section = form?.sections?.[state.position.section];
  const questionId = section?.questionIds?.[state.position.question];

  return (
    state.exam.questions?.find((q: any) => {
      return q.id === questionId;
    }) ?? undefined
  );
};

const Exam: React.FC<ExamPlayerProps> = (props) => {
  const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<any>();
  const [item, setItem] = useState<Resource>();
  const handle = useFullScreenHandle();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [highlighterEnabled, setHighlighterEnabled] = useState(false);
  const [calculatorEnabled, setCalculatorEnabled] = useState(false);

  // this was the easiest way to always ensure 1 line reader is active
  // at a time (and it lets me repaint the line reader when the font changes)
  const [lineReaderEnabled, setLineReaderEnabled] = useState(false);
  const [activeLineReader, setActiveLineReader] = useState<LineReader | null>(
    null,
  );
  const [isResponseMaskEnabled, setIsResponseMaskEnabled] = useState(false);

  const [state, dispatch] = useImmerReducer<ExamState, ExamReducerActions>(
    reducer,
    {
      ...defaultState,
      exam: props.examData,
      responses: props.responses,
      blobs: props.blobs,
      position: props.position ?? defaultState.position,
      highlights: props.highlights,
    },
  );

  /** @todo consider refactor to remove dispatch from a useEffect based on value of a prop. */
  useEffect(() => {
    dispatch({
      type: "SET_PAUSE",
      isPaused: props.isPaused ?? false,
    });
  }, [dispatch, props.isPaused]);

  const shuffleItemsSeed = props.sesssionData?.shuffleItems
    ? props.sesssionData?.shuffleSeed
    : undefined;
  useEffect(() => {
    if (!props.examData) return;

    let exam = props.examData;
    // shuffle the section exam data if needed
    if (shuffleItemsSeed) {
      exam = produce(exam, (draft: ParsedExam) => {
        if (!draft.content?.forms) return draft;
        draft.content.forms = draft.content.forms.map((form) => {
          return {
            ...form,
            sections: form?.sections?.map((section) => {
              return {
                ...section,
                questionIds: shuffleArray(
                  shuffleItemsSeed,
                  section?.questionIds ?? [],
                ),
              };
            }),
          };
        });
      });
    }

    dispatch({
      type: "SET_EXAM",
      data: exam,
    });
  }, [dispatch, props.examData, shuffleItemsSeed]);

  useEffect(() => {
    const questionId = getExamItemIdAtPosition(
      state.exam,
      state.position.form,
      state.position.section,
      state.position.question,
    );

    if (!questionId) {
      setItem(undefined);
      return;
    }

    setItem(
      state.exam?.questions?.find((q) => {
        return q?.id === questionId;
      }) ?? undefined,
    );
  }, [
    state.position.form,
    state.position.section,
    state.position.question,
    state.exam,
  ]);

  const handleItemChange = useCallback(
    (rspData: any) => {
      if (item?.id) {
        dispatch({
          type: "SET_ITEM_RESPONSE",
          itemId: item.id,
          response: rspData,
        });
      }
    },
    [dispatch, item?.id],
  );
  const handleBlobChange = useCallback(
    (blobData: ExamBlobs[string]) => {
      if (!item?.id) return;
      dispatch({
        type: "SET_ITEM_BLOB",
        data: {
          id: item.id,
          blobs: blobData,
        },
      });
    },
    [dispatch, item?.id],
  );

  useAutoSave(state, (state: ExamState) => {
    if (props.onSave) {
      props.onSave(state);
    }
  });

  // Where the magic happens
  useResizeObserver(ref, (entry) => {
    setSize(entry.contentRect);
    dispatch({
      type: "SET_WINDOW_SIZE",
      windowSize: entry.contentRect,
    });
  });

  const sectionNumber = state?.position.section + 1;
  const questionNumber = state?.position.question + 1;

  useEffect(() => {
    if (props.onNavigation) {
      props.onNavigation({
        form: 0,
        section: sectionNumber,
        question: questionNumber,
      });
    }
    // reset the response masking state when the question changes
    setIsResponseMaskEnabled(false);
  }, [questionNumber, sectionNumber, props.onNavigation]);

  return (
    <ExamFlagsContext.Provider value={props.flags}>
      <ExamConfigContext.Provider value={props.config}>
        <ExamToolsContext.Provider
          value={{
            isResponseMasking: isResponseMaskEnabled,
          }}
        >
          <ExamContext.Provider value={[state, dispatch]}>
            <HighlighterContext.Provider
              value={{
                enabled: highlighterEnabled,
              }}
            >
              <LineReaderContext.Provider
                value={{
                  enabled: lineReaderEnabled,
                  activeLineReader,
                  setActiveLineReader,
                }}
              >
                <RichContentOptionsProvider
                  options={
                    //
                    // delegate form to override default options set at the app level
                    //
                    () => ({
                      tools: {
                        advancedFormatting: true,
                        blockFormatting: true,
                        math: true,
                      },
                    })
                  }
                >
                  {calculatorEnabled && (
                    <Calculator
                      type={validateSupportedCalculatorType(
                        props.config?.enableCalculatorType,
                      )}
                    />
                  )}
                  <StyledFullScreen handle={handle}>
                    <Container>
                      <StyledExam ref={ref} padding={props.padding}>
                        <Modal
                          isOpen={state.isPaused || false}
                          title="Activity Paused"
                          onRequestClose={() => {
                            // @todo Consider refactoring the isPaused prop into
                            // isTeacherPaused or isForcePaused or something. Then do
                            // <Modal isOpen={props.isTeacherPaused || state.isPaused
                            // || false} /> and save yourself all of this dispatching
                            if (props.isPaused) return;
                            dispatch({
                              type: "SET_PAUSE",
                              isPaused: false,
                            });
                          }}
                          buttons={
                            <PrimaryButton
                              disabled={props.isPaused}
                              onClick={() =>
                                dispatch({
                                  type: "SET_PAUSE",
                                  isPaused: false,
                                })
                              }
                            >
                              <div style={{ padding: "1.5rem 1rem" }}>
                                Resume
                              </div>
                            </PrimaryButton>
                          }
                        >
                          <StyledMessage>
                            Click Resume to continue your activity.
                          </StyledMessage>
                        </Modal>
                        <Modal
                          isOpen={state.isPausedWithExitPrompt || false}
                          title="Activity Saved"
                          onRequestClose={() => {
                            dispatch({
                              type: "SET_PAUSED_WITH_EXIT_PROMPT",
                              value: false,
                            });
                          }}
                          buttons={
                            <Toolbar>
                              <SecondaryButton
                                disabled={props.isPaused}
                                onClick={() =>
                                  dispatch({
                                    type: "SET_PAUSED_WITH_EXIT_PROMPT",
                                    value: false,
                                  })
                                }
                              >
                                <div style={{ padding: "1.5rem 1rem" }}>
                                  Resume
                                </div>
                              </SecondaryButton>
                              <PrimaryButton
                                disabled={props.isPaused}
                                onClick={() => props.onExit?.()}
                              >
                                <div style={{ padding: "1.5rem 1rem" }}>
                                  Exit
                                </div>
                              </PrimaryButton>
                            </Toolbar>
                          }
                        >
                          <StyledMessage>
                            Your assessment has been saved. Click Exit to close
                            this assessment, or click Resume to continue.
                          </StyledMessage>
                        </Modal>
                        <Modal
                          title="Ready to Submit?"
                          isOpen={isFinishModalOpen}
                          onRequestClose={() => setIsFinishModalOpen(false)}
                          buttons={
                            <div style={{ display: "flex", gap: "1rem" }}>
                              {!isSubmitting && (
                                <SecondaryButton
                                  onClick={() => setIsFinishModalOpen(false)}
                                >
                                  Cancel
                                </SecondaryButton>
                              )}
                              <PrimaryButton
                                disabled={isSubmitting}
                                onClick={async () => {
                                  setIsSubmitting(true);
                                  await props.onSubmit?.(state);
                                  setIsSubmitting(false);
                                  setIsFinishModalOpen(false);
                                  dispatch({
                                    type: "COMPLETE",
                                  });
                                }}
                              >
                                {isSubmitting ? "Please Wait..." : "Submit"}
                              </PrimaryButton>
                            </div>
                          }
                        >
                          <div style={{ padding: "1rem 1rem" }}>
                            Are you ready to submit your activity?
                          </div>
                        </Modal>
                        <Header
                          onFinish={() => setIsFinishModalOpen(true)}
                          isFinished={!!props.results}
                          disableSubmit={props.disableSubmit}
                          disablePause={props.disablePause}
                        />
                        {props.view === "instructor" && item != null && (
                          <StyledItemNameCallout>
                            <b style={{ display: "block" }}>
                              {item.name ?? ""}
                              {item.idInc && ` | #${item.idInc}`}
                            </b>
                            <i>
                              Item titles and question IDs do not display to
                              students.
                            </i>
                          </StyledItemNameCallout>
                        )}
                        <ExamMain>
                          {!props.results && (
                            <>
                              <ExamMainColumn>
                                <ItemContainer
                                  fontSize={
                                    state.accessibility?.fontSize || "normal"
                                  }
                                >
                                  {item && (
                                    <Item
                                      data={item}
                                      sessionData={props.sesssionData}
                                      response={state.responses?.[item.id]}
                                      blobs={state.blobs?.[item.id]}
                                      onChange={handleItemChange}
                                      onBlobChange={handleBlobChange}
                                    />
                                  )}
                                </ItemContainer>
                              </ExamMainColumn>
                              {size?.width > 700 && (
                                <>
                                  {!!state?.exam?.content?.forms?.[0]?.sections
                                    ?.length && (
                                    <ExamSideColumn>
                                      <Navigation
                                        items={state?.exam?.content?.forms[0]}
                                        flaggedItemIds={state.flaggedItemIds}
                                        onRequestEditItem={
                                          props.onRequestEditItem
                                            ? (position: ItemPosition) => {
                                                const pos =
                                                  getExamItemIdAtPosition(
                                                    state.exam,
                                                    position.form,
                                                    position.section,
                                                    position.question,
                                                  );
                                                if (!pos) {
                                                  console.error(
                                                    "No exam item position found",
                                                  );
                                                  return;
                                                }
                                                props.onRequestEditItem?.(pos);
                                              }
                                            : undefined
                                        }
                                        currentPosition={[
                                          state.position.form,
                                          state.position.section,
                                          state.position.question,
                                        ].join(".")}
                                        onItemClick={(e, position) => {
                                          dispatch({
                                            type: "SET_POSITION",
                                            position,
                                          });
                                        }}
                                      />
                                    </ExamSideColumn>
                                  )}
                                </>
                              )}
                              <ExamTools>
                                <Tools
                                  allowSaveAndExit={
                                    !!props.config?.allowSaveAndExit
                                  }
                                  saveAndExit={() => {
                                    props.onSave?.(state);
                                    dispatch({
                                      type: "SET_PAUSED_WITH_EXIT_PROMPT",
                                      value: true,
                                    });
                                  }}
                                  isFlagged={state.flaggedItemIds?.includes(
                                    item?.id ?? "",
                                  )}
                                  toggleFlagged={() => {
                                    dispatch({
                                      type: "TOGGLE_FLAG",
                                      itemId: item?.id,
                                    });
                                  }}
                                  canResponseMask={
                                    !!item && itemSupportsResponseMasking(item)
                                  }
                                  isResponseMaskEnabled={isResponseMaskEnabled}
                                  setIsResponseMaskEnabled={
                                    setIsResponseMaskEnabled
                                  }
                                  isLineReaderEnabled={lineReaderEnabled}
                                  setIsLineReaderEnabled={(value) => {
                                    setHighlighterEnabled(false);
                                    setLineReaderEnabled(value);
                                  }}
                                  isHighlighterEnabled={highlighterEnabled}
                                  setIsHighlighterEnabled={(value) => {
                                    setLineReaderEnabled(false);
                                    setHighlighterEnabled(value);
                                  }}
                                  isCalculatorEnabled={calculatorEnabled}
                                  setIsCalculatorEnabled={(value) => {
                                    setCalculatorEnabled(value);
                                  }}
                                  canIncreaseFont={
                                    state.accessibility?.fontSize !== "huge"
                                  }
                                  increaseFont={() => {
                                    dispatch({
                                      type: "INCREASE_FONT",
                                    });
                                    // repaint the line reader after the font change
                                    // animation finishes.
                                    setTimeout(() => {
                                      if (activeLineReader) {
                                        activeLineReader.paint();
                                      }
                                    }, 500);
                                  }}
                                  canDecreaseFont={
                                    state.accessibility?.fontSize !== "normal"
                                  }
                                  decreaseFont={() => {
                                    dispatch({
                                      type: "DECREASE_FONT",
                                    });
                                    // repaint the line reader after the font change
                                    // animation finishes
                                    setTimeout(() => {
                                      if (activeLineReader) {
                                        activeLineReader.paint();
                                      }
                                    }, 500);
                                  }}
                                  allowCalculator={
                                    !!props.config?.enableCalculatorType
                                  }
                                  allowLineReader={
                                    props.flags.lineReader &&
                                    props.config?.enableLineReader
                                  }
                                  allowHighlighter={
                                    props.flags.highlighter &&
                                    props.config?.enableHighlighter
                                  }
                                  allowFeedback={props.allowFeedback}
                                  allowResponseMask={
                                    props.flags.responseMasking &&
                                    props.config?.enableOptionEliminator
                                  }
                                  previewUserId={props.previewUserId}
                                />
                              </ExamTools>
                            </>
                          )}
                          {props.results && (
                            <>
                              {/* 
                                if results.item is empty, it means we failed to fetch 
                                their scores and we should show the NoResults component
                              */}
                              {!props.config?.showScoreOnComplete ||
                              props.results.items?.length === 0 ? (
                                <NoResults
                                  config={props.config}
                                  onRequestRetake={() => {
                                    if (
                                      !window.confirm(
                                        "Are you sure you want to retake?",
                                      )
                                    ) {
                                      return;
                                    }
                                    dispatch({
                                      type: "RESET",
                                    });
                                    props.onRequestRetake?.();
                                  }}
                                />
                              ) : (
                                <Results
                                  results={props.results}
                                  responses={state.responses ?? {}}
                                  blobs={state.blobs ?? {}}
                                  config={props.config ?? {}}
                                  toolbar={props.resultsToolbar}
                                  sessionData={props.sesssionData}
                                  itemPreviewer={props.itemPreviewer}
                                  onRequestRetake={() => {
                                    if (
                                      !window.confirm(
                                        "Are you sure you want to retake?",
                                      )
                                    ) {
                                      return;
                                    }
                                    dispatch({
                                      type: "RESET",
                                    });
                                    props.onRequestRetake?.();
                                  }}
                                />
                              )}
                            </>
                          )}
                        </ExamMain>
                      </StyledExam>
                    </Container>
                  </StyledFullScreen>
                </RichContentOptionsProvider>
              </LineReaderContext.Provider>
            </HighlighterContext.Provider>
          </ExamContext.Provider>
        </ExamToolsContext.Provider>
      </ExamConfigContext.Provider>
    </ExamFlagsContext.Provider>
  );
};

export default Exam;
